import './TermsCondition.css';

const GeneralPrivacyNotice = () => {
  return (
    <div className="main-section main-body-bg">
      <div className="privacy-notice">
        <div className="container">
          <h1 className="h1-basic01 tc-head">General Privacy Notice</h1><br />

          <p id="intro-paragraph">
            You come into contact with us when you use a service in which our technology is integrated, usually through a partner that provides the relevant service. This privacy notice explains how we collect, process and protect your personal data when you visit our website, contact us through forms, use our services through a partner or otherwise interact with us.
          </p>

          {/* Document Information Table */}
          <div className="main-section">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <td><strong>Document name</strong></td>
                  <td>General Privacy Notice</td>
                </tr>
                <tr>
                  <td><strong>Type of document</strong></td>
                  <td>Notice</td>
                </tr>
                <tr>
                  <td><strong>Approval date</strong></td>
                  <td>2025-09-18</td>
                </tr>
                <tr>
                  <td><strong>Version of this document</strong></td>
                  <td>1.0</td>
                </tr>
                <tr>
                  <td><strong>Document owner</strong></td>
                  <td>CEO</td>
                </tr>
                <tr>
                  <td><strong>Classification</strong></td>
                  <td>Public</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Table of Contents */}
          <div className="main-section">
            <h3>Table of Contents</h3>
            <div className="info-main">
              <div className="toc-item">
                <a href="#introduction">1. Introduction</a>
                <span>3</span>
              </div>
              <div className="toc-item">
                <a href="#personal-data-processing">2. Personal data processing</a>
                <span>3</span>
              </div>
              <div className="toc-item" style={{ paddingLeft: '20px' }}>
                <a href="#contacting-kreditz">2.1. When contacting Kreditz</a>
                <span>4</span>
              </div>
              <div className="toc-item" style={{ paddingLeft: '20px' }}>
                <a href="#account-information-services">2.2. As a user of our account information services</a>
                <span>5</span>
              </div>
              <div className="toc-item" style={{ paddingLeft: '20px' }}>
                <a href="#swedish-tax-agency">2.3. When retrieving information from the Swedish Tax Agency</a>
                <span>6</span>
              </div>
              <div className="toc-item" style={{ paddingLeft: '20px' }}>
                <a href="#accounting-systems">2.4. When retrieving data from accounting systems</a>
                <span>7</span>
              </div>
              <div className="toc-item" style={{ paddingLeft: '20px' }}>
                <a href="#anti-money-laundering">2.5. Processing in accordance with the Anti-Money Laundering Act</a>
                <span>8</span>
              </div>
              <div className="toc-item">
                <a href="#legal-bases">3. Different legal bases for processing your personal data</a>
                <span>8</span>
              </div>
              <div className="toc-item">
                <a href="#data-sharing">4. Who we share your data with</a>
                <span>9</span>
              </div>
              <div className="toc-item">
                <a href="#international-transfers">5. International Data Transfers</a>
                <span>10</span>
              </div>
              <div className="toc-item">
                <a href="#data-storage">6. Storage of personal data</a>
                <span>10</span>
              </div>
              <div className="toc-item">
                <a href="#security-measures">7. Security Measures</a>
                <span>10</span>
              </div>
              <div className="toc-item">
                <a href="#your-rights">8. Your rights</a>
                <span>11</span>
              </div>
              <div className="toc-item">
                <a href="#supervisory-authority">9. Supervisory authority</a>
                <span>11</span>
              </div>
              <div className="toc-item">
                <a href="#contact-us">10. Contact us</a>
                <span>11</span>
              </div>
            </div>
          </div>

          <div className="main-section">
            <h2 id="introduction">1. Introduction</h2>
            <p>
              Kreditz AB, registration number 559148-2400, Vendevägen 87, 182 32 Danderyd, Stockholm ("Kreditz", "we", "us") is a Swedish company that provides digital services for the collection, structuring and analysis of financial information. Our services are used, among other things, for identity verification, regulatory compliance or as part of credit and other assessments. We have permission from the Financial Supervisory Authority (Sw. Finansinspektionen) to provide account information services.
            </p>
            <p>
              You come into contact with us when you use a service in which our technology is integrated, usually through a partner that provides the relevant service. This privacy notice explains how we collect, process and protect your personal data when you visit our website, contact us through forms, use our services through a partner or otherwise interact with us. More information about how we process personal data can be found under point 2.
            </p>
            <p>
              The privacy notice also covers the processing that takes place when Kreditz fulfills its obligations under the Act (2017:630) on measures against money laundering and financing of terrorism (the <strong> “Anti-Money Laundering Act”</strong> or <strong>“AML”</strong>).
            </p>
            <p>
              For those of you who contact us in connection with a <strong>recruitment process</strong> (e.g. by submitting an application or expression of interest), please see our separate privacy notice for <a href="https://careers.kreditz.com/privacy-policy" target="_blank">recruitment</a>.
            </p>
            <p>
              We use cookies and similar technologies on our website to improve functionality, analyze user behavior and, in some cases, for marketing purposes. You can choose which cookies you agree to, and you can change or withdraw your consent at any time. More information about how we use cookies and how you manage your settings can be found in our <a href="https://careers.kreditz.com/cookie" target="_blank">Cookie Policy</a>.
            </p>
            <p>
              We have appointed a <strong>data protection officer</strong> which will help us ensure that we meet these requirements. If you have any questions regarding how we fulfill our obligations, you are welcome to contact our data protection officer using the contact details in section 10 below.
            </p>
          </div>

          <div className="main-section">
            <h2 id="personal-data-processing">2. Processing of personal data</h2>
            <p>Below you can read about how we collect and process personal data in different parts of our operations. We describe what we do with the data depending on how you interact with us.</p>
            <p>For each area, we explain which categories of personal data may be processed, why and for what purpose the data is processed, and what role we have hold under Regulation (EU) 2016/679 of the European Parliament and of the Council, also known as the general data protection regulation <strong>("GDPR")</strong>.</p>
          </div>
          {/* 2.1 */}
          <div className="inner-section">
            <h3 id="contacting-kreditz">2.1. When contacting Kreditz</h3>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Category</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Context</h5>
                </div>
                <div className="info-para">
                  <p>When you visit our website, contact us via a form, subscribe to our newsletters, consent to be contacted for marketing purposes, or visit our office.</p>

                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Personal data processed</h5>
                </div>
                <div className="info-para">
                  <p>
                    - Contact details (name, email address, phone number)
                  </p>
                  <p>
                    - IP address and technical information about your device
                  </p>
                  <p>
                    - Camera surveillance with image recording during physical visits
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Purpose of the processing</h5>
                </div>
                <div className="info-para">
                  <p>- Responding to inquiries and feedback</p>
                  <p>- Manage subscriptions and marketing communications</p>
                  <p>- Analyze website usage</p>
                  <p>- Ensure technical operation and security</p>
                  <p>- Protecting the company through physical security</p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Our role under GDPR</h5>
                </div>
                <div className="info-para">
                  <p>
                    Kreditz is the data controller for this processing. We determine the purposes and means of the processing and are responsible for ensuring that the processing is carried out in accordance with GDPR.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Additional information</h5>
                </div>
                <div className="info-para">
                  <p>
                    See our Cookie Policy for more details on how we use cookies and technical data.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 2.2 */}
          <div className="inner-section">
            <h3 id="account-information-services">2.2. When using our account information services</h3>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Category</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Context</h5>
                </div>
                <div className="info-para">
                  <p>
                    When you use a service where our technology is used to retrieve
                    account information from your bank or account management institute,
                    we process personal data on behalf of that partner (e.g. a
                    bank, lender or other service provider) whose service you
                    are using.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Personal data processed</h5>
                </div>
                <div className="info-para">
                  <p>
                    - Contact details (name, email, phone number)
                  </p>
                  <p>
                    - Personal identification number
                  </p>
                  <p>
                    - Account information (e.g. bank, balances, transactions)
                  </p>
                  <p>
                    - Technical information (IP address, device type, browser)
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Purpose of the processing</h5>

                </div>
                <div className="info-para">
                  <p>- Retrieve, structure and analyze account information</p>
                  <p>- Generate financial insights as part of our partner's
                    credit assessment</p>
                  <p>- Ensure technical function and safety</p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Our role under GDPR</h5>

                </div>
                <div className="info-para">
                  <p>
                    Kreditz is the data controller for the step involving the retrieval of
                    account information from your bank or account management institute.
                    Thereafter, for the further processing of your personal data, we act as a
                    data processor on behalf of our partner, who is the data controller.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 2.3 */}
          <div className="inner-section">
            <h3 id="swedish-tax-agency">2.3. When retrieving information from the Swedish Tax Agency</h3>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Category</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Context</h5>
                </div>
                <div className="info-para">
                  <p>
                    When you use a service where Kreditz, on behalf of the partner
                    whose service you are using, retrieves information from the Swedish Tax Agency.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Personal data processed</h5>
                </div>
                <div className="info-para">
                  <p>
                    - Contact details (name, email, phone number)
                  </p>
                  <p>
                    - Personal identification number
                  </p>
                  <p>
                    - Income details
                  </p>
                  <p>
                    - Employer information
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Purpose of the processing</h5>

                </div>
                <div className="info-para">
                  <p>- Form part of the basis used by the partner for
                    credit assessment
                  </p>
                  <p>- Verify employment</p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Our role under GDPR</h5>

                </div>
                <div className="info-para">
                  <p>
                    Kreditz is a data processor and processes your data on
                    behalf of our partner, who is the data controller.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 2.4 */}
          <div className="inner-section">
            <h3 id="accounting-systems">2.4. When retrieving data from accounting systems</h3>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Category</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Context</h5>
                </div>
                <div className="info-para">
                  <p>
                    When you use a service where Kreditz, on behalf of the partner
                    whose service you use, retrieves data from an accounting system.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Documentation and personal data processed</h5>
                </div>
                <div className="info-para">
                  <p>
                    - Profit and loss statement and balance sheet
                  </p>
                  <p>
                    - Accounting records that may contain indirect personal data
                  </p>
                  <p>
                    - Information on assets, liabilities and equity
                  </p>
                  <p>
                    - Other relevant financial information needed to assess
                    the company's finances
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Purpose of the processing</h5>

                </div>
                <div className="info-para">
                  <p>
                    - To form part of the basis for assessing the financial
                    situation before granting credit
                  </p>
                  <p>
                    - To verify the company's financial position, income and
                    solvency
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Our role under GDPR</h5>
                </div>
                <div className="info-para">
                  <p>
                    Kreditz is a data processor and processes your data on
                    behalf of our partner, who is the data controller.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 2.4 */}
          <div className="inner-section">
            <h3 id="anti-money-laundering">2.5. Processing in accordance with the Anti-Money Laundering Act</h3>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Category</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Context</h5>
                </div>
                <div className="info-para">
                  <p>
                    When we process personal data to fulfill our legal
                    obligations under applicable rules of the Anti-Money Laundering Act.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Personal data processed</h5>
                </div>
                <div className="info-para">
                  <p>
                    - Name, personal identification number, contact details
                  </p>
                  <p>
                    - ID documents
                  </p>
                  <p>
                    - Purpose and nature of the business relationship
                  </p>
                  <p>
                    - Information on politically exposed person and beneficial owner
                  </p>
                  <p>
                    - Transaction history and customer relationship with Kreditz
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Purpose of the processing</h5>

                </div>
                <div className="info-para">
                  <p>
                    - Know your customer (KYC)
                  </p>
                  <p>
                    - Risk assessment and monitoring of business relationships
                  </p>
                  <p>
                    - To report suspicious activities to the competent authority
                  </p>
                  <p>
                    - To comply with legal requirements to prevent money laundering and
                    terrorist financing
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Our role under GDPR</h5>
                </div>
                <div className="info-para">
                  <p>
                    Kreditz acts as the data controller. Processing is done independently
                    and according to legal obligations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="main-section">
            <h2 id="legal-bases">3. Legal bases for processing of your personal data</h2>
            <div className="info-main">
              <div className="info-details">
                <div className="info-title">
                  <h5>Legal basis</h5>
                </div>
                <div className="info-para">
                  <h5>
                    Description
                  </h5>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Consent</h5>
                </div>
                <div className="info-para">
                  <p>
                    We process your personal data based on your explicit
                    consent when:
                  </p>
                  <p>
                    - You use our account information service
                  </p>
                  <p>
                    - You agree to us retrieving information from the Swedish Tax Agency or
                    accounting system
                  </p>
                  <p>
                    - You accept cookies on our website
                  </p>
                  <p>
                    - You sign up for a newsletter or agree to marketing contact
                  </p>
                  <p>
                    You can withdraw your consent at any time. The withdrawal
                    does not affect the lawfulness of the processing prior to the withdrawal.
                  </p>
                  <p>
                    If you use our account information service and wish to withdraw
                    your consent, please click <a target="_blank" href="https://kreditz.com/consent-withdrawal?locale=en">here</a>.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Legal obligation</h5>
                </div>
                <div className="info-para">
                  <p>
                    We are required to process certain personal data in order to fulfill
                    legal requirements, in particular when acting as a data controller for
                    processing according to AML. This includes:
                  </p>
                  <p>
                    - Perform know your customer (KYC)
                  </p>
                  <p>
                    - Risk assessment and customer identification
                  </p>
                  <p>
                    - Reporting to competent authorities
                  </p>
                  <p>
                    - Retain documentation for a certain period of time
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Agreement</h5>

                </div>
                <div className="info-para">
                  <p>
                    Processing occurs when necessary to fulfill an agreement
                    between you and Kreditz or the partner whose service you use.
                  </p>
                </div>
              </div>
              <div className="info-details">
                <div className="info-title">
                  <h5>Legitimate interest</h5>
                </div>
                <div className="info-para">
                  <p>
                    We process personal data to meet our legitimate
                    interests when:
                  </p>
                  <p>
                    - We communicate with you
                  </p>
                  <p>
                    - We analyze website usage

                  </p>
                  <p>
                    - We secure our technical operation and information security

                  </p>
                  <p>
                    - We use surveillance camera at our premises

                  </p>
                  <p>
                    - We may anonymize personal data to improve
                    our models and quality of services
                  </p>
                  <p>
                    In any such processing, we will weigh the interests of the data subject
                    to ensure that your rights do not outweigh our interests.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="main-section">
            <h2 id="data-sharing">4. Who we share your data with</h2>
            <div className="info-main">
              <p>
                We share your personal data with service providers acting as our
                data processors. They are only permitted to process your data according to our instructions and may
                not use it for their own purposes. These recipients belong to the following categories:
              </p>
              <div className="padding-left-5">
                <p>
                  - Cloud service providers for hosting and data storage
                </p>
                <p>
                  - Email communication services
                </p>
                <p>
                  - SMS communication platforms
                </p>
                <p>
                  - Marketing services for email distribution
                </p>
                <p>
                  - Services for optimization of our services and website
                </p>
                <p>
                  - Security and development services
                </p>
              </div>
              <p>
                In certain cases, we are obliged to share personal data with competent authorities when required
                by the Anti-Money Laundering Act. Such sharing is done without your prior consent and may occur
                without informing you.
              </p>
              <p>
                We do not sell or share your personal data with any other third parties.
              </p>
            </div>
          </div>

          <div className="main-section">
            <h2 id="international-transfers">5. International data transfers</h2>
            <div className="info-main">
              <p>
                If we transfer your personal data outside the European Economic Area (EEA), we ensure appropriate safeguards, such as the use of the European Commission's Standard Contractual Clauses (SCC).
              </p>
            </div>
          </div>

          <div className="main-section">
            <h2 id="data-storage">6. Storage of personal data</h2>
            <div className="info-main">
              <p>
                We only retain your personal data for as long as necessary for the specific purpose or to fulfill legal requirements. Personal data is anonymized or  when no longer needed.
              </p>
              <p>
                When processing is carried out under applicable legislation, such as AML, the Bookkeeping Act and the Annual Accounts Act, we retain personal data in accordance with legal requirements.
              </p>
            </div>
          </div>

          <div className="main-section">
            <h2 id="security-measures">7. Security measures</h2>
            <div className="info-main">
              <p>
                Kreditz, in its capacity as a data processor, has implemented the following technical and organizational security measures:
              </p>
              <div className="padding-left-5">
                <p>
                  - Encryption of personal data both during at rest and in transit
                </p>
                <p>
                  - Access controls with authorization role-based permissions
                </p>
                <p>
                  - Continuous system monitoring and audit logging
                </p>
                <p>
                  - Secure development practices and regular security testing
                </p>
                <p>
                  - Internal trainings, governance documents, and policies for privacy and information security
                </p>
                <p>
                  - Measures for data minimization
                </p>
              </div>
            </div>
          </div>

          <div className="main-section">
            <h2 id="your-rights">8. Your rights</h2>
            <div className="info-main">
              <p>
                You have the right to:
              </p>
              <div className="padding-left-5">
                <p>
                  - Request access to your data
                </p>
                <p>
                  - Request correction of incorrect data
                </p>
                <p>
                  - Request deletion of your data ("right to be forgotten")
                </p>
                <p>
                  - Object to certain types of processing
                </p>
                <p>
                  - Request data portability
                </p>
              </div>
              <p>
                Contact us via the contact details below if you wish to exercise any of these rights.
              </p>
            </div>
          </div>
          <div className="main-section">
            <h2 id="supervisory-authority">9. Supervisory authority</h2>
            <div className="info-main">
              <p>
                If you believe your personal data is being processed incorrectly, or if your rights under the GDPR have not been respected, you can file a complaint with the relevant supervisory authority:
              </p>
              <p>
                <strong>Swedish Authority for Privacy Protection (Sw. Integritetsskyddsmyndigheten)</strong><br />
                Website: <a href="https://www.imy.se" target="_blank">www.imy.se</a>
              </p>
            </div>
          </div>

          <div className="main-section">
            <h2 id="contact-us">10. Contact us</h2>
            <div className="info-main">
              <p>
                If you have questions about this privacy notice or how we process your personal data, you can contact us at:
              </p>
              <p>
                Data Protection Officer<br />
                Kreditz AB<br />
                Vendevägen 87, 182 32 Danderyd
              </p>
              <p>
                E-mail: <a href="mailto:privacy@kreditz.com">privacy@kreditz.com</a>
              </p>
            </div>
          </div>

          <div className="main-section">
            <h2>Version history</h2>
            <div className="info-main">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Updated by</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

export default GeneralPrivacyNotice;